/**
 * A confirmation that cannot be clicked through.
 *
 * The OK button is disabled for a few seconds and counts down **on itself**, because the failure this
 * guards is not recoverable by us: an app built on the machine of another partner's Google Play account
 * puts two accounts on one machine, and Google bans them together (§7). A dialog you can dismiss with
 * the same reflex that opened it does not protect anything — the delay exists to make the reader
 * actually read the sentence naming the app and the machine.
 *
 * Deliberately not the `ConfirmDelete` pattern (type the name + solve a sum): that one guards a
 * destructive action on *our* data, where the cost is a re-registered phone. This one guards a choice
 * that is easy to make correctly and expensive to make wrong, so it asks for attention, not effort.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from './scrollLock'

import { useT } from '../i18n'

export function ConfirmDanger({
  title,
  children,
  confirmLabel,
  holdSeconds = 5,
  blocked = false,
  onConfirm,
  onCancel,
}: {
  title: string
  children: ReactNode
  confirmLabel: string
  holdSeconds?: number
  /** The dialog asks for something not given yet (a choice, a reason): confirming would only be refused. */
  blocked?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  useScrollLock()
  const t = useT()
  const [left, setLeft] = useState(holdSeconds)

  useEffect(() => {
    if (left <= 0) return
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000)
    return () => window.clearTimeout(id)
  }, [left])

  // Escape cancels; there is deliberately no Enter-to-confirm, which would restore the reflex the
  // countdown exists to break.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  // Rendered into <body>, not where it was called from. It is opened from a table cell, and a cell
  // brings its own layout with it: `col-machine` sets `white-space: nowrap`, which the dialog inherited
  // and which stopped the warning sentence wrapping — the text ran out of the box and pushed the
  // buttons off it entirely. A portal makes the dialog independent of wherever it was triggered.
  return createPortal(
    <div className="scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="dialog">
        <h3>{title}</h3>
        <div className="warn-box">{children}</div>
        <div className="actions">
          <button className="ghost" onClick={onCancel}>
            {t.confirm.cancel}
          </button>
          <button className="danger" disabled={left > 0 || blocked} onClick={onConfirm}>
            {left > 0 ? `${confirmLabel} (${left})` : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
